import { useEffect, useState } from 'react';

import { useUnit } from 'effector-react';

import { fetchTodoItems, todoStore } from 'apps/customer/state/todo';
import { userStore } from 'apps/customer/state/user';
import { visitStore } from 'apps/customer/state/visit';

// Retries for the first todo load: everything after sign-in (routing, onboarding) waits on it, so
// one transient failure would otherwise leave the customer on a blank screen.
const INITIAL_LOAD_RETRY_DELAYS_MS = [1000, 3000, 10000];

/** fetchTodoItems, retried after each delay while it fails and `isCancelled()` stays false. */
async function fetchTodoItemsWithRetries(delaysMs: number[], isCancelled: () => boolean) {
  const response = await fetchTodoItems().catch(() => null);
  if (response?.success || delaysMs.length === 0 || isCancelled()) return;
  await new Promise((resolve) => setTimeout(resolve, delaysMs[0]));
  if (isCancelled()) return;
  await fetchTodoItemsWithRetries(delaysMs.slice(1), isCancelled);
}

export function useRefreshTodoItems() {
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  const { visits } = useUnit(visitStore);
  const { user } = useUnit(userStore);
  const {
    hasPendingTasks,
    status: { fetchTodoItemsPending },
  } = useUnit(todoStore);

  const { id: userId } = user;

  const { transactionPaymentStatus } = visits?.[0] || {};

  const { id: transactionPaymentStatusId } = transactionPaymentStatus || {};

  // Only fetch todos if we didn't receive todos from the initialize API (eqless QR flow)
  useEffect(() => {
    if (userId !== currentUserId) {
      setCurrentUserId(userId || null);
      if (userId) {
        // First time todos get fetched, will have been called due to userId being set in useUserInitialization
        // via handleQrCode and parseQRDelegation (assuming all went well)
        // The retries stop once someone else is signed in (or nobody is).
        void fetchTodoItemsWithRetries(
          INITIAL_LOAD_RETRY_DELAYS_MS,
          () => userStore.getState().user?.id !== userId,
        );
      }
    }
  }, [userId, currentUserId]);

  // Re-visit to see when the best time to fetch a todo is
  // Checks todo if visit payment status changes
  // Handles case where user has app open and leaves the site. we want to show the alert icon
  useEffect(() => {
    // If already fetching todos or has pending tasks, don't fetch again
    if (transactionPaymentStatusId && !fetchTodoItemsPending && !hasPendingTasks) {
      fetchTodoItems();
    }
    // Purposely leaving out other deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionPaymentStatusId]);
}
